import React, { useEffect, useMemo, useState } from "react";
import Box from "@mui/material/Box";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import CircularProgress from "@mui/material/CircularProgress";
import Grid from "@mui/material/Grid";
import Typography from "@mui/material/Typography";
import { useAppDispatch, useAppSelector } from "../../../framework/customStore";
import { VehiclePositionInfo } from "../../../models/vehicle/vehiclePosition";
import { VehicleListItemDto } from "../../../models/transport/vehicle";
import { useTranslation } from "react-i18next";
import {
    getVehicleStatusColorClass,
    getVehicleStatusDuration,
    getVehicleStatusText,
} from "../monitorViewUtils";
import {
    getWorkTimeTypeColorClass,
    getWorkTimeTypeColorCode,
} from "../../../models/workShitTimeSlot/workTimeType";
import {
    EnumHelper,
    WorkShiftTimeSlotState,
} from "../../../models/common/enums";
import Tooltip from "@mui/material/Tooltip";
import CircleIcon from "@mui/icons-material/Circle";
import PersonIcon from "@mui/icons-material/Person";
import LocalShippingIcon from "@mui/icons-material/LocalShipping";
import VideocamIcon from "@mui/icons-material/Videocam";
import VideocamOffIcon from "@mui/icons-material/VideocamOff";
import CameraStreamDialog from "./fleetOverviewCard/cameraStreamDialog";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import WorkIcon from "@mui/icons-material/Work";
import CreditCardIcon from "@mui/icons-material/CreditCard";
import SpeedIcon from "@mui/icons-material/Speed";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import BusinessIcon from "@mui/icons-material/Business";
import AccountBalanceIcon from "@mui/icons-material/AccountBalance";
import SyncIcon from "@mui/icons-material/Sync";
import { positionStreetAddressReq } from "../../../models/geocode/positionTypes";
import {
    fetchGeocodedVehicleAddress,
    fetchCurrentEmployeeWorkShift,
} from "../../../store/monitorViewSlice";
import { AppUtils } from "../../../models/common/appUtils";
import { Base } from "../../../framework/base";
import { TransportOrderListItemDto } from "../../../models/transport/transportOrder";
import VehicleOrdersSection from "./fleetOverviewCard/vehicleOrdersSection";
import Divider from "@mui/material/Divider";

const iconSx = { fontSize: 16, color: "text.secondary" };

const DetailRow = ({
    icon,
    label,
    value,
    valueBold,
    valueColor,
}: {
    icon: React.ReactNode;
    label?: string;
    value: React.ReactNode;
    valueBold?: boolean;
    valueColor?: string;
}) => (
    <Box display="flex" alignItems="center" gap={0.5} sx={{ mt: 0.25 }}>
        {icon}
        {label && (
            <Typography variant="caption" color="text.secondary" noWrap>
                {label}
            </Typography>
        )}
        <Typography
            variant="caption"
            noWrap
            sx={{
                fontWeight: valueBold ? "bold" : "normal",
                color: valueColor || "text.primary",
            }}
        >
            {value}
        </Typography>
    </Box>
);

interface MonitorViewVehicleCurrentStatusProps {
    transportOrders?: TransportOrderListItemDto[];
}

export const MonitorViewVehicleCurrentStatus = ({
    transportOrders,
}: MonitorViewVehicleCurrentStatusProps) => {
    const loading = useAppSelector((state) => state.monitorView.loading);
    const positions = useAppSelector((state) => state.monitorView.positions);
    const vehicles = useAppSelector((state) => state.monitorView.vehicles);
    const selectedVehicleId = useAppSelector(
        (state) => state.monitorView.selectedVehicleId
    );

    const vehicleOrders = useMemo(() => {
        if (!transportOrders?.length || !selectedVehicleId) return [];
        const now = new Date();
        return transportOrders.filter((o) => {
            if (
                !o.vehicleId ||
                o.vehicleId.toLowerCase() !== selectedVehicleId.toLowerCase()
            )
                return false;
            const start = o.scheduledStartDateTime
                ? new Date(o.scheduledStartDateTime)
                : null;
            const end = o.scheduledEndDateTime
                ? new Date(o.scheduledEndDateTime)
                : null;
            if (start && start > now) return false;
            if (end && end < now) return false;
            return true;
        });
    }, [transportOrders, selectedVehicleId]);

    return (
        <>
            {loading ? (
                <Box display="flex" justifyContent="center" p={2}>
                    <CircularProgress />
                </Box>
            ) : (
                <>
                    <VehicleCurrentDetails
                        lastPosition={positions.find(
                            (p) => p.vehicleId === selectedVehicleId
                        )}
                        vehicleInfo={vehicles.find(
                            (p) => p.id === selectedVehicleId
                        )}
                    />
                    {vehicleOrders.length > 0 && (
                        <Box sx={{ px: 0.5, pb: 0.5 }}>
                            <Divider sx={{ mb: 0.5 }} />
                            <VehicleOrdersSection orders={vehicleOrders} />
                        </Box>
                    )}
                </>
            )}
        </>
    );
};

interface VehicleCurrentDetailsProps {
    lastPosition?: VehiclePositionInfo;
    vehicleInfo?: VehicleListItemDto;
}

const getDurationFromNow = (startTime: string): string => {
    const durationMinutes = Base.dayjsToMinuteAccuracy().diff(Base.dayjsToMinuteAccuracy(startTime), "minute");
    return AppUtils.getDurationStrByDurationMinShort(durationMinutes, true);
};

const VehicleCurrentDetails = ({
    lastPosition,
    vehicleInfo,
}: VehicleCurrentDetailsProps) => {
    const { t } = useTranslation();
    const dispatch = useAppDispatch();
    const [cameraDialogOpen, setCameraDialogOpen] = useState(false);
    const vehicleCameras = useAppSelector((state) => state.vehicle.vehicleCameras);
    const vehicleStreetAddress = useAppSelector(
        (state) => state.monitorView.vehicleStreetAddress
    );
    const vehicleAddressLoading = useAppSelector(
        (state) => state.monitorView.vehicleAddressLoading
    );
    const currentEmployeeWorkShift = useAppSelector(
        (state) => state.monitorView.currentEmployeeWorkShift
    );
    const currentEmployeeWorkShiftLoading = useAppSelector(
        (state) => state.monitorView.currentEmployeeWorkShiftLoading
    );

    const cameraData = useMemo(() => {
        if (!vehicleInfo?.id || !vehicleCameras) return null;
        return vehicleCameras.find((c) => c.vehicleId === vehicleInfo.id) ?? null;
    }, [vehicleInfo?.id, vehicleCameras]);

    const hasCamera = cameraData?.cameraDevices?.length > 0;

    const employeeId = lastPosition?.employeeWorkTimeTypes?.[0]?.employeeId;

    useEffect(() => {
        if (!vehicleInfo?.id || !lastPosition?.latitude || !lastPosition?.longitude) return;

        const addressRequest: positionStreetAddressReq = {
            id: vehicleInfo.id,
            lat: lastPosition.latitude,
            lon: lastPosition.longitude,
        };

        dispatch(fetchGeocodedVehicleAddress(addressRequest) as any);
    }, [vehicleInfo?.id, lastPosition?.latitude, lastPosition?.longitude, dispatch]);

    useEffect(() => {
        if (!employeeId) return;
        dispatch(fetchCurrentEmployeeWorkShift(employeeId) as any);
    }, [employeeId, dispatch]);

    const inProgressSlot = useMemo(() => {
        if (!currentEmployeeWorkShift?.workShiftTimeSlots) return null;
        return currentEmployeeWorkShift.workShiftTimeSlots.find((slot) =>
            EnumHelper.isEqual(slot.state, WorkShiftTimeSlotState.InProgress)
        );
    }, [currentEmployeeWorkShift]);

    const borderColor =
        inProgressSlot?.workTimeTypeType !== null &&
        inProgressSlot?.workTimeTypeType !== undefined
            ? getWorkTimeTypeColorCode(inProgressSlot.workTimeTypeType)
            : undefined;

    return (
        <Box sx={{ px: 0.5, pt: 0.5 }}>
            {/* Vehicle + employee + work status card */}
            <Card
                sx={{
                    mb: 1,
                    ...(borderColor
                        ? { borderLeft: `4px solid ${borderColor}` }
                        : {}),
                }}
            >
                <CardContent sx={{ p: 1, "&:last-child": { pb: 1 } }}>
                    {/* Vehicle status header */}
                    <Grid
                        container
                        justifyContent="space-between"
                        alignItems="center"
                    >
                        <Grid item display="flex" alignItems="center" gap={0.5}>
                            <LocalShippingIcon sx={iconSx} />
                            <Typography variant="body2" fontWeight="bold">
                                {getVehicleStatusText(lastPosition?.driving, t)}
                            </Typography>
                        </Grid>
                        <Grid item>
                            <Typography
                                variant="body2"
                                fontWeight="bold"
                                className={`text-${getVehicleStatusColorClass(lastPosition?.driving)}`}
                            >
                                {getVehicleStatusDuration(
                                    lastPosition?.drivingGmt
                                )}
                            </Typography>
                        </Grid>
                    </Grid>
                    <Box display="flex" justifyContent="space-between" alignItems="center">
                        <DetailRow
                            icon={<SpeedIcon sx={iconSx} />}
                            value={`${lastPosition?.speed ?? 0} km/h`}
                        />
                        {vehicleInfo && (
                            <Tooltip
                                title={
                                    hasCamera
                                        ? t("monitorView.camera.hasCamera")
                                        : t("monitorView.camera.noCamera")
                                }
                            >
                                <span
                                    onClick={() => {
                                        if (hasCamera) setCameraDialogOpen(true);
                                    }}
                                    style={hasCamera ? { cursor: "pointer" } : undefined}
                                >
                                    {hasCamera ? (
                                        <VideocamIcon fontSize="small" color="primary" />
                                    ) : (
                                        <VideocamOffIcon fontSize="small" color="disabled" />
                                    )}
                                </span>
                            </Tooltip>
                        )}
                    </Box>
                    {hasCamera && cameraData && (
                        <CameraStreamDialog
                            open={cameraDialogOpen}
                            onClose={() => setCameraDialogOpen(false)}
                            vehicleRegisterNumber={vehicleInfo?.registerNumber ?? ""}
                            cameraData={cameraData}
                        />
                    )}
                    <DetailRow
                        icon={<LocationOnIcon sx={iconSx} />}
                        value={
                            vehicleAddressLoading ? (
                                <SyncIcon
                                    sx={{
                                        fontSize: 14,
                                        animation: "spin 1s linear infinite",
                                    }}
                                />
                            ) : (
                                vehicleStreetAddress || "-"
                            )
                        }
                    />
                    <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ mt: 0.25, display: "block" }}
                    >
                        {lastPosition?.latitude}, {lastPosition?.longitude}
                    </Typography>

                    {/* Employee & work details */}
                    <Box
                        sx={{
                            mt: 0.5,
                            pt: 0.5,
                            borderTop: "1px solid #e0e0e0",
                        }}
                    >
                        {currentEmployeeWorkShiftLoading ? (
                            <Box display="flex" justifyContent="center" p={0.5}>
                                <CircularProgress size={18} />
                            </Box>
                        ) : (
                            <EmployeeWorkSection
                                employeeName={
                                    lastPosition?.employeeWorkTimeTypes?.[0]
                                        ?.employeeName
                                }
                                hasEmployee={!!employeeId}
                                workShiftStartTime={
                                    currentEmployeeWorkShift?.startTime
                                }
                                inProgressSlot={inProgressSlot}
                            />
                        )}
                    </Box>
                </CardContent>
            </Card>

            {/* Tachograph card (separate - supplementary info) */}
            {(lastPosition?.driverGmt || lastPosition?.codriverGmt) && (
                <Card sx={{ mb: 1 }}>
                    <CardContent sx={{ p: 1, "&:last-child": { pb: 1 } }}>
                        {lastPosition?.driverGmt && (
                            <DetailRow
                                icon={<CreditCardIcon sx={iconSx} />}
                                label={t(
                                    "monitorView.tachograph.driverCardInserted"
                                )}
                                value={Base.dateStrToOriginalTimezoneTimeStr(
                                    lastPosition.driverGmt
                                )}
                            />
                        )}
                        {lastPosition?.codriverGmt && (
                            <DetailRow
                                icon={<CreditCardIcon sx={iconSx} />}
                                label={t(
                                    "monitorView.tachograph.codriverCardInserted"
                                )}
                                value={Base.dateStrToOriginalTimezoneTimeStr(
                                    lastPosition.codriverGmt
                                )}
                            />
                        )}
                    </CardContent>
                </Card>
            )}
        </Box>
    );
};

interface EmployeeWorkSectionProps {
    employeeName?: string;
    hasEmployee: boolean;
    workShiftStartTime?: string;
    inProgressSlot?: {
        customerName?: string;
        costCenterName?: string;
        workTimeTypeName?: string;
        workTimeTypeType?: number;
        startDate?: string;
    };
}

const EmployeeWorkSection = ({
    employeeName,
    hasEmployee,
    workShiftStartTime,
    inProgressSlot,
}: EmployeeWorkSectionProps) => {
    const { t } = useTranslation();
    return (
        <>
            {/* Employee name */}
            <DetailRow
                icon={<PersonIcon sx={iconSx} />}
                value={employeeName || t("monitorView.noDriver")}
                valueBold
            />

            {!hasEmployee || !inProgressSlot ? (
                <DetailRow
                    icon={
                        <WorkIcon sx={{ ...iconSx, color: "text.disabled" }} />
                    }
                    value={t("monitorView.workTime.noActiveWorkTime")}
                    valueColor="text.secondary"
                />
            ) : (
                <>
                    {/* Work phase */}
                    <Box
                        display="flex"
                        alignItems="center"
                        gap={0.5}
                        sx={{ mt: 0.5 }}
                    >
                        <CircleIcon
                            className={`text-${getWorkTimeTypeColorClass(inProgressSlot.workTimeTypeType)}`}
                            sx={{ fontSize: 14 }}
                        />
                        <Typography variant="body2" fontWeight="bold">
                            {inProgressSlot.workTimeTypeName}
                        </Typography>
                        {inProgressSlot.startDate && (
                            <Typography
                                variant="caption"
                                color="text.secondary"
                                sx={{ ml: 0.5 }}
                            >
                                {Base.dateStrToOriginalTimezoneTimeStr(
                                    inProgressSlot.startDate
                                )}
                            </Typography>
                        )}
                        {inProgressSlot.startDate && (
                            <Typography variant="caption" fontWeight="bold">
                                {getDurationFromNow(inProgressSlot.startDate)}
                            </Typography>
                        )}
                    </Box>

                    {/* Work shift time */}
                    {workShiftStartTime && (
                        <DetailRow
                            icon={<AccessTimeIcon sx={iconSx} />}
                            label={t("monitorView.workTime.workShift")}
                            value={`${Base.dateStrToOriginalTimezoneTimeStr(workShiftStartTime)} (${getDurationFromNow(workShiftStartTime)})`}
                        />
                    )}

                    {/* Customer */}
                    {inProgressSlot.customerName && (
                        <DetailRow
                            icon={<BusinessIcon sx={iconSx} />}
                            value={inProgressSlot.customerName}
                        />
                    )}

                    {/* Cost center */}
                    {inProgressSlot.costCenterName && (
                        <DetailRow
                            icon={<AccountBalanceIcon sx={iconSx} />}
                            value={inProgressSlot.costCenterName}
                        />
                    )}
                </>
            )}
        </>
    );
};
